/**
 * GuideBoardDirectionHud
 *
 * 안내현황판 좌/우 영역 드래그 모드일 때만 보이는 하단 중앙 안내 오버레이.
 * 현재 단계(① 왼쪽 / ② 오른쪽)를 표시하고 취소 버튼을 제공한다.
 * ZoneDrawingHud 와 동일하게 인라인 스타일로 가볍게 구현.
 */

import { type CSSProperties } from 'react'
import type { GuideBoardLinkPhase } from '../hooks/useGuideBoardDirectionLink'

interface GuideBoardDirectionHudProps {
  phase: GuideBoardLinkPhase
  onCancel: () => void
}

const hudStyle: CSSProperties = {
  position: 'fixed',
  left: '50%',
  bottom: 24,
  transform: 'translateX(-50%)',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '10px 16px',
  background: 'rgba(31, 41, 55, 0.95)',
  color: '#f3f4f6',
  borderRadius: 10,
  boxShadow: '0 8px 28px rgba(0, 0, 0, 0.32)',
  zIndex: 1000,
  fontSize: 13,
  lineHeight: 1.3,
  userSelect: 'none',
}

const dotStyle = (color: string): CSSProperties => ({
  display: 'inline-block',
  width: 10,
  height: 10,
  borderRadius: 2,
  background: color,
  marginRight: 6,
  verticalAlign: 'middle',
})

const cancelButtonStyle: CSSProperties = {
  padding: '6px 14px',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  border: '1px solid rgba(229, 231, 235, 0.35)',
  background: 'transparent',
  color: '#e5e7eb',
  lineHeight: 1.2,
}

const startButtonStyle: CSSProperties = {
  position: 'fixed',
  left: '50%',
  bottom: 24,
  transform: 'translateX(-50%)',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 18px',
  background: '#3A8EEE',
  color: '#ffffff',
  border: 'none',
  borderRadius: 10,
  boxShadow: '0 8px 28px rgba(0, 0, 0, 0.32)',
  zIndex: 1000,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  userSelect: 'none',
}

interface GuideBoardLinkButtonProps {
  onStart: () => void
}

/**
 * 안내현황판이 선택됐을 때 보이는 진입 버튼. 누르면 좌/우 영역 드래그 모드 시작.
 * ("자동 관계 생성" 버튼은 객체 선택 시 패널에서 숨겨지므로 별도 진입점 제공)
 */
export function GuideBoardLinkButton({ onStart }: GuideBoardLinkButtonProps) {
  return (
    <button type="button" style={startButtonStyle} onClick={onStart}>
      🚥 안내현황판 좌/우 주차구역 관계 맺기
    </button>
  )
}

export function GuideBoardDirectionHud({
  phase,
  onCancel,
}: GuideBoardDirectionHudProps) {
  if (phase === 'idle') return null

  const isLeft = phase === 'left'
  const color = isLeft ? '#3B82F6' : '#10B981'

  return (
    <div style={hudStyle}>
      <span>
        <span style={dotStyle(color)} />
        안내현황판 관계 —{' '}
        <strong>{isLeft ? '① 왼쪽' : '② 오른쪽'}</strong> 영역을 드래그하세요
        {!isLeft && ' (드래그하면 완료)'}
      </span>
      <button type="button" style={cancelButtonStyle} onClick={onCancel}>
        취소 (Esc)
      </button>
    </div>
  )
}
